import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  NgbDate,
  NgbDropdown,
  NgbOffcanvas,
  NgbOffcanvasRef,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@ngneat/transloco';
import { AbstractComponent } from '../abstract.component';

type YM = { year: number; month: number };

@Component({
  selector: 'app-month-picker-mobile',
  templateUrl: './month-picker-mobile.component.html',
  styleUrls: ['./month-picker-mobile.component.scss'],
  standalone: false,
})
export class MonthPickerMobileComponent extends AbstractComponent {
  @Input() title: string = '';
  @Input() minDate: NgbDate | undefined;
  @Input() maxDate: NgbDate | undefined;
  @Input() chosenFrom?: { from?: NgbDate };
  @Input() chosenTo?: { to?: NgbDate };

  @Output() monthsSelected = new EventEmitter<{ from?: NgbDate; to?: NgbDate }>();

  @ViewChild('offcanvasContent') offcanvasContent?: TemplateRef<any>;
  @ViewChild('desktopDropdown', { static: false }) desktopDropdown?: NgbDropdown;

  private offcanvasRef?: NgbOffcanvasRef;

  today = new Date();
  currentMonth = this.today.getMonth();
  currentYearNow = this.today.getFullYear();

  currentYear = new Date().getFullYear();
  startMonth?: YM;
  endMonth?: YM;

  months = [
    this.translocoService.translate('months.picker.short.january'),
    this.translocoService.translate('months.picker.short.february'),
    this.translocoService.translate('months.picker.short.march'),
    this.translocoService.translate('months.picker.short.april'),
    this.translocoService.translate('months.picker.short.may'),
    this.translocoService.translate('months.picker.short.june'),
    this.translocoService.translate('months.picker.short.july'),
    this.translocoService.translate('months.picker.short.august'),
    this.translocoService.translate('months.picker.short.september'),
    this.translocoService.translate('months.picker.short.october'),
    this.translocoService.translate('months.picker.short.november'),
    this.translocoService.translate('months.picker.short.december'),
  ];

  monthsDisabled: Map<number, boolean> = new Map<number, boolean>();

  constructor(private translocoService: TranslocoService, private offcanvasService: NgbOffcanvas) {
    super();
  }

  ngOnInit(): void {
    for (let month = 1; month <= 12; month++) {
      this.monthsDisabled.set(month, this.isDisabled(month));
    }
    this.subscriptions.push(this.isMobileSubject.subscribe(_ => {
      this.onFilterClose();
    }))
  }

  ngOnChanges(ch: SimpleChanges): void {
    if ('chosenFrom' in ch || 'chosenTo' in ch) {
      const f = this.chosenFrom?.from;
      const t = this.chosenTo?.to;

      if (f && t) {
        this.startMonth = { year: f.year, month: f.month - 1 };
        this.endMonth = { year: t.year, month: t.month - 1 };
      } else if (f) {
        this.startMonth = { year: f.year, month: f.month - 1 };
        this.endMonth = undefined;
      } else if (t) {
        this.startMonth = { year: t.year, month: t.month - 1 };
        this.endMonth = undefined;
      } else {
        this.startMonth = undefined;
        this.endMonth = undefined;
        this.currentYear = new Date().getFullYear();
        this.markDisabled();
        return;
      }

      this.currentYear = this.startMonth ? this.startMonth.year : new Date().getFullYear();
      this.markDisabled();
    }

    if ('minDate' in ch || 'maxDate' in ch) {
      this.markDisabled();
    }
  }

  @HostListener('window:scroll')
  onScroll() {
    if (this.isBrowser) {
      this.desktopDropdown?.close();
    }
  }

  onClear(): void {
    this.startMonth = undefined;
    this.endMonth = undefined;
    this.emitRange();
    this.onFilterClose();
  }

  onClose(): void {
    this.onFilterClose();
  }

  private isLessOrEqualYM(a: YM, b: YM): boolean {
    return a.year < b.year || (a.year === b.year && a.month <= b.month);
  }

  selectMonth(monthIndex0Based: number): void {
    const clicked: YM = { year: this.currentYear, month: monthIndex0Based };

    if (!this.startMonth || this.endMonth) {
      this.startMonth = clicked;
      this.endMonth = undefined;
    } else {
      if (this.isLessOrEqualYM(clicked, this.startMonth)) {
        this.endMonth = this.startMonth;
        this.startMonth = clicked;
      } else {
        this.endMonth = clicked;
      }
    }
  }

  private emitRange(): void {
    const range = this.getSelectedRange();
    this.monthsSelected.emit(range ?? { from: undefined, to: undefined });
  }

  getSelectedRange(): { from?: NgbDate; to?: NgbDate } | undefined {
    if (!this.startMonth) return undefined;

    const start = this.startMonth;
    const end = this.endMonth ?? this.startMonth;

    const startNgbMonth = start.month + 1;
    const endNgbMonth = end.month + 1;

    let from: NgbDate;
    if (this.minDate && start.year === this.minDate.year && startNgbMonth === this.minDate.month) {
      from = this.minDate;
    } else {
      from = new NgbDate(start.year, startNgbMonth, 1);
    }

    let to: NgbDate;
    const lastDay = new Date(end.year, endNgbMonth, 0).getDate();
    if (this.maxDate && end.year === this.maxDate.year && endNgbMonth === this.maxDate.month) {
      to = this.maxDate;
    } else {
      to = new NgbDate(end.year, endNgbMonth, lastDay);
    }

    if (from.after(to)) return { from: to, to: from };
    return { from, to };
  }

  isInRange(monthIndex0Based: number): boolean {
    if (!this.startMonth) return false;

    const start = this.startMonth;
    const end = this.endMonth ?? this.startMonth;
    const cell: YM = { year: this.currentYear, month: monthIndex0Based };

    return this.isLessOrEqualYM(start, cell) && this.isLessOrEqualYM(cell, end);
  }


  isDisabled(month1Based: number): boolean {
    if (!this.minDate && !this.maxDate) return false;

    if (this.minDate && this.currentYear < this.minDate.year) return true;
    if (this.maxDate && this.currentYear > this.maxDate.year) return true;

    if (this.minDate && this.currentYear === this.minDate.year && month1Based < this.minDate.month) {
      return true;
    }
    if (this.maxDate && this.currentYear === this.maxDate.year && month1Based > this.maxDate.month) {
      return true;
    }

    return false;
  }

  markDisabled(): void {
    for (let month = 1; month <= 12; month++) {
      this.monthsDisabled.set(month, this.isDisabled(month));
    }
  }

  prevYear(): void {
    this.currentYear--;
    this.markDisabled();
  }

  nextYear(): void {
    this.currentYear++;
    this.markDisabled();
  }

  onApply(): void {
    this.emitRange();
    this.onClose();
  }

  onOpenMobileFilter(): void {
    if (!this.isBrowser) return;
    if (!this.offcanvasContent) return;

    this.startMonth = this.chosenFrom?.from
        ? { year: this.chosenFrom.from.year, month: this.chosenFrom.from.month - 1 }
        : undefined;
    this.endMonth = this.chosenTo?.to
        ? { year: this.chosenTo.to.year, month: this.chosenTo.to.month - 1 }
        : undefined;

    this.currentYear = this.startMonth?.year ?? this.today.getFullYear();
    this.markDisabled();

    this.offcanvasRef = this.offcanvasService.open(this.offcanvasContent, {
      position: 'bottom',
      panelClass: 'mobile-filter-sheet',
      backdropClass: 'offcanvas-backdrop'
    });
  }

  onDesktopDropdownOpen(isOpen: boolean): void {
    if (isOpen) {
      this.startMonth = this.chosenFrom?.from
          ? { year: this.chosenFrom.from.year, month: this.chosenFrom.from.month - 1 }
          : undefined;
      this.endMonth = this.chosenTo?.to
          ? { year: this.chosenTo.to.year, month: this.chosenTo.to.month - 1 }
          : undefined;

      this.currentYear = this.startMonth?.year ?? this.today.getFullYear();
      this.markDisabled();
    }
  }

  onFilterClose(): void {
    this.desktopDropdown?.close();
    this.offcanvasRef?.close();
  }
}